import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Container, Typography, Paper, Chip, Grid, Button, Collapse, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeMode } from '../contexts/ThemeContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import {
  Speed,
  Security,
  Refresh,
  Hub,
  AutoAwesome,
  PlayCircleOutline,
  Close,
} from '@mui/icons-material';

/* ═══════════════════════════════════════════════════════════════════
   WorkflowAnimation — Premium animated workflow orchestration demo
   ═══════════════════════════════════════════════════════════════════ */

// ─── Palette ─────────────────────────────────────────────────────
const P = {
  light: {
    canvasBg: 'rgba(255,255,255,0.22)',
    canvasBorder: 'rgba(99, 102, 241,0.06)',
    canvasShadow: '0 12px 48px rgba(99, 102, 241,0.05)',
    // nodes
    nodeBg: 'rgba(255,255,255,0.60)',
    nodeShine: 'rgba(255,255,255,0.92)',
    httpStroke: 'rgba(99,102,241,0.30)',
    httpAccent: '#6366f1',
    httpGlow: 'rgba(99,102,241,0.08)',
    dbStroke: 'rgba(16,185,129,0.30)',
    dbAccent: '#10b981',
    dbGlow: 'rgba(16,185,129,0.08)',
    iconHttp: '#6366f1',
    iconDb: '#059669',
    // mapping
    mapBg: 'rgba(255,255,255,0.65)',
    mapStroke: 'rgba(148,163,184,0.20)',
    mapIcon: '#64748b',
    // connector dots
    port: '#06b6d4',
    // line
    line: '#94a3b8',
    pulse: '#6366f1',
    returnLine: '#818cf8',
    // arrow between mapping & node
    arrow: 'rgba(139,92,246,0.45)',
    // label
    labelBg: 'rgba(255,255,255,0.88)',
    labelStroke: 'rgba(148,163,184,0.15)',
    labelText: '#1e293b',
    // section
    title: '#1e293b',
    sub: '#64748b',
    pillBorder: 'rgba(99, 102, 241,0.10)',
    pillActive: 'rgba(99, 102, 241,0.07)',
    pillText: '#475569',
    pillAccent: '#6366f1',
  },
  dark: {
    canvasBg: 'rgba(15,23,42,0.28)',
    canvasBorder: 'rgba(165,180,252,0.06)',
    canvasShadow: '0 12px 48px rgba(0,0,0,0.30)',
    nodeBg: 'rgba(30,41,59,0.55)',
    nodeShine: 'rgba(71,85,105,0.30)',
    httpStroke: 'rgba(129,140,248,0.30)',
    httpAccent: '#818cf8',
    httpGlow: 'rgba(129,140,248,0.10)',
    dbStroke: 'rgba(52,211,153,0.30)',
    dbAccent: '#34d399',
    dbGlow: 'rgba(52,211,153,0.10)',
    iconHttp: '#a5b4fc',
    iconDb: '#34d399',
    mapBg: 'rgba(30,41,59,0.60)',
    mapStroke: 'rgba(148,163,184,0.12)',
    mapIcon: '#94a3b8',
    port: '#22d3ee',
    line: '#475569',
    pulse: '#a78bfa',
    returnLine: '#a5b4fc',
    arrow: 'rgba(196,181,253,0.40)',
    labelBg: 'rgba(15,23,42,0.85)',
    labelStroke: 'rgba(148,163,184,0.10)',
    labelText: '#e2e8f0',
    title: '#f1f5f9',
    sub: '#94a3b8',
    pillBorder: 'rgba(165,180,252,0.08)',
    pillActive: 'rgba(165,180,252,0.06)',
    pillText: '#94a3b8',
    pillAccent: '#a5b4fc',
  },
};

// ─── Icons (24×24) ───────────────────────────────────────────────
const ICO = {
  globe:    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
  database: 'M12 3C7.58 3 4 4.79 4 7v10c0 2.21 3.58 4 8 4s8-1.79 8-4V7c0-2.21-3.58-4-8-4zm0 2c3.87 0 6 1.5 6 2s-2.13 2-6 2-6-1.5-6-2 2.13-2 6-2zM6 17v-2.34c1.37.93 3.55 1.34 6 1.34s4.63-.41 6-1.34V17c0 .5-2.13 2-6 2s-6-1.5-6-2zm0-5v-2.34c1.37.93 3.55 1.34 6 1.34s4.63-.41 6-1.34V12c0 .5-2.13 2-6 2s-6-1.5-6-2z',
  swap:     'M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z',
};

// ─── Steps ───────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: 'Connect Source', desc: 'Incoming API connects instantly — authentication & pooling handled automatically' },
  { id: 1, label: 'Add Datasource', desc: 'Link any datasource in seconds — SQL, NoSQL, REST, GraphQL — just configure, no code' },
  { id: 2, label: 'Logic Builder', desc: 'Open the visual Logic Builder to map, transform, and validate data with drag-and-drop' },
  { id: 3, label: 'External Service', desc: 'Connect unlimited external APIs — Alphanetix manages retries, fallbacks & circuit breakers' },
  { id: 4, label: 'Logic Builder', desc: 'Create logic between datasources to transform, vaidate and map the data via drag and drop' },
  { id: 5, label: 'Complete Flow', desc: 'Data flows back to the caller — connection pools released, errors handled gracefully' },
];

// ─── Feature highlights shown below the canvas ───────────────────
const FEATURES = [
  { Icon: Speed, title: 'Auto Connection Pools', desc: 'Managed pooling for every datasource — zero config, zero overhead' },
  { Icon: Security, title: 'Built-in Auth', desc: 'OAuth, API keys, JWT — configured once, applied everywhere' },
  { Icon: Refresh, title: 'Auto Fallbacks', desc: 'Retries & circuit breakers out of the box for every connection' },
  { Icon: Hub, title: 'Unlimited Sources', desc: 'Connect N datasources via simple config — no driver management' },
];

// ─── SVG Defs ────────────────────────────────────────────────────
const Defs = ({ c, isLight }) => (
  <defs>
    <filter id="fNode" x="-25%" y="-25%" width="150%" height="150%">
      <feDropShadow dx="0" dy="4" stdDeviation="10"
        floodColor={isLight ? 'rgba(99, 102, 241,0.07)' : 'rgba(0,0,0,0.30)'} floodOpacity="1" />
    </filter>
    <filter id="fNodeDb" x="-25%" y="-25%" width="150%" height="150%">
      <feDropShadow dx="0" dy="4" stdDeviation="10"
        floodColor={isLight ? 'rgba(16,185,129,0.07)' : 'rgba(0,0,0,0.30)'} floodOpacity="1" />
    </filter>
    <filter id="fMap" x="-25%" y="-25%" width="150%" height="150%">
      <feDropShadow dx="0" dy="2" stdDeviation="6"
        floodColor={isLight ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.20)'} floodOpacity="1" />
    </filter>
    <filter id="fLabel" x="-15%" y="-15%" width="130%" height="130%">
      <feDropShadow dx="0" dy="1" stdDeviation="3"
        floodColor={isLight ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.20)'} floodOpacity="1" />
    </filter>
    <filter id="fPulse" x="-100%" y="-100%" width="300%" height="300%">
      <feGaussianBlur stdDeviation="3" result="b" />
      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
    {/* border gradients */}
    <linearGradient id="gHttp" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor={c.httpAccent} stopOpacity="0.4" />
      <stop offset="100%" stopColor={c.port} stopOpacity="0.15" />
    </linearGradient>
    <linearGradient id="gDb" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor={c.dbAccent} stopOpacity="0.4" />
      <stop offset="100%" stopColor={c.port} stopOpacity="0.15" />
    </linearGradient>
    {/* arrow marker for forward flow */}
    <marker id="mArrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
      <polygon points="0 0, 8 3, 0 6" fill={c.arrow} />
    </marker>
    <marker id="mArrowRet" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
      <polygon points="0 0, 8 3, 0 6" fill={c.returnLine} opacity="0.5" />
    </marker>
  </defs>
);

// ─── Connector Dot ───────────────────────────────────────────────
const Dot = ({ cx, cy, color, r = 4, delay = 0 }) => (
  <motion.circle cx={cx} cy={cy} r={r} fill={color}
    initial={{ scale: 0 }} animate={{ scale: 1 }}
    transition={{ delay, duration: 0.3, type: 'spring', stiffness: 300 }} />
);

// ─── Data Node (HTTP / Database) ─────────────────────────────────
const Node = ({ x, y, type, label, c, isLight, delay = 0, vis = false,
                portL = false, portR = false, portT = false }) => {
  if (!vis) return null;
  const isHttp = type === 'http';
  const W = 76, H = 76, R = 18;
  const hw = W / 2, hh = H / 2;
  const accent = isHttp ? c.httpAccent : c.dbAccent;
  const glow   = isHttp ? c.httpGlow   : c.dbGlow;
  const icon   = isHttp ? ICO.globe    : ICO.database;
  const iconC  = isHttp ? c.iconHttp   : c.iconDb;
  const border = isHttp ? 'url(#gHttp)' : 'url(#gDb)';
  const filter = isHttp ? 'url(#fNode)' : 'url(#fNodeDb)';

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.3 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.65, type: 'spring', stiffness: 200, damping: 20 }}
    >
      {/* Breathing glow ring */}
      <motion.rect x={x-hw-2} y={y-hh-2} width={W+4} height={H+4}
        rx={R+2} fill="none" stroke={accent} strokeWidth={0.8}
        opacity={0}
        animate={{ opacity: [0, 0.18, 0] }}
        transition={{ delay: delay+0.8, duration: 3, repeat: Infinity, repeatDelay: 4 }}
      />
      {/* Card */}
      <rect x={x-hw} y={y-hh} width={W} height={H}
        rx={R} fill={c.nodeBg} stroke={border} strokeWidth={1.2} filter={filter} />
      {/* Icon halo */}
      <circle cx={x} cy={y+1} r={20} fill={glow} />
      <circle cx={x} cy={y+1} r={15} fill="none" stroke={accent} strokeWidth={0.6} opacity={0.15} />
      {/* Icon */}
      <g transform={`translate(${x-12},${y-11})`}>
        <path d={icon} fill={iconC} />
      </g>
      {/* Connector dots — only where lines touch */}
      {portL && <Dot cx={x-hw} cy={y} color={c.port} delay={delay+0.35} />}
      {portR && <Dot cx={x+hw} cy={y} color={c.port} delay={delay+0.35} />}
      {portT && <Dot cx={x} cy={y-hh} color={c.port} delay={delay+0.35} />}
      {/* Label */}
      <motion.g initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: delay+0.5, duration: 0.35 }}>
        <rect x={x - label.length*3.8 - 12} y={y+hh+10}
          width={label.length*7.6 + 24} height={24}
          rx={12} fill={c.labelBg} stroke={c.labelStroke} strokeWidth={0.8}
          filter="url(#fLabel)" />
        <text x={x} y={y+hh+26} textAnchor="middle"
          fill={c.labelText} fontSize={11} fontWeight={600}
          fontFamily="Montserrat, sans-serif">
          {label}
        </text>
      </motion.g>
    </motion.g>
  );
};

// ─── Mapping Node (Logic Builder) ────────────────────────────────
const MapNode = ({ x, y, c, isLight, delay = 0, vis = false, small = false, label = '' }) => {
  if (!vis) return null;
  const sz = small ? 32 : 40;
  const half = sz / 2;
  const r = small ? 8 : 10;

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.45, type: 'spring', stiffness: 280, damping: 22 }}
    >
      <rect x={x-half} y={y-half} width={sz} height={sz}
        rx={r} fill={c.mapBg} stroke={c.mapStroke} strokeWidth={0.8}
        filter="url(#fMap)" />
      {/* Icon */}
      <g transform={`translate(${x-(small?7:9)},${y-(small?7:9)}) scale(${small?0.58:0.75})`}>
        <path d={ICO.swap} fill={c.mapIcon} />
      </g>
      {/* Side connectors (line passes through these) */}
      <Dot cx={x-half} cy={y} color={c.port} r={3.5} delay={delay+0.2} />
      <Dot cx={x+half} cy={y} color={c.port} r={3.5} delay={delay+0.2} />
      {/* Label below */}
      {label && !small && (
        <motion.text x={x} y={y+half+14} textAnchor="middle"
          fill={c.mapIcon} fontSize={8.5} fontWeight={600}
          fontFamily="Montserrat, sans-serif"
          initial={{ opacity: 0 }} animate={{ opacity: 0.7 }}
          transition={{ delay: delay+0.3 }}>
          {label}
        </motion.text>
      )}
    </motion.g>
  );
};

// ─── Flow Line (single clean line between two points) ────────────
const FlowLine = ({ x1, y1, x2, y2, c, delay = 0, vis = false }) => {
  if (!vis) return null;
  return (
    <motion.line x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={c.line} strokeWidth={1.5}
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 0.55 }}
      transition={{ delay, duration: 0.7, ease: 'easeOut' }}
    />
  );
};

// ─── Directional Arrow (triangle between mapping and next node) ──
const FlowArrow = ({ x, y, c, delay = 0, vis = false, flip = false }) => {
  if (!vis) return null;
  const pts = flip
    ? `${x+5},${y-4} ${x+5},${y+4} ${x-5},${y}`
    : `${x-5},${y-4} ${x-5},${y+4} ${x+5},${y}`;
  return (
    <motion.polygon points={pts} fill={c.arrow}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.3 }}
    />
  );
};

// ─── Animated Pulse (data packet traveling along a line) ─────────
const Pulse = ({ x1, y1, x2, y2, c, delay = 0, vis = false }) => {
  if (!vis) return null;
  return (
    <motion.circle r={3.5} fill={c.pulse} filter="url(#fPulse)"
      initial={{ cx: x1, cy: y1, opacity: 0 }}
      animate={{ cx: [x1, x2], cy: [y1, y2], opacity: [0, 0.9, 0.9, 0] }}
      transition={{ delay, duration: 1.8, repeat: Infinity, repeatDelay: 2.5, ease: 'easeInOut' }}
    />
  );
};

// ─── Return Arc ──────────────────────────────────────────────────
const ReturnArc = ({ from, to, peakY, c, delay = 0, vis = false }) => {
  if (!vis) return null;
  const d = `M${from.x},${from.y} C${from.x},${peakY} ${to.x},${peakY} ${to.x},${to.y}`;
  return (
    <motion.path d={d} stroke={c.returnLine} strokeWidth={1.5}
      strokeDasharray="7 4" fill="none" markerEnd="url(#mArrowRet)"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 0.45 }}
      transition={{ delay, duration: 1.3, ease: 'easeInOut' }}
    />
  );
};

// ─── Return Pulse (data packet along the full return arc) ────────
const ReturnPulse = ({ pathD, c, vis = false }) => {
  if (!vis) return null;
  return (
    <circle r={3.5} fill={c.returnLine} filter="url(#fPulse)" opacity="0.85">
      <animateMotion dur="3.5s" repeatCount="indefinite" fill="freeze">
        <mpath href="#returnFullPath" />
      </animateMotion>
    </circle>
  );
};

// ─── Layout ──────────────────────────────────────────────────────
const layout = (w, h, mob) => {
  if (mob) {
    const cx = w / 2;
    return {
      http1: { x: cx, y: 70 },
      db:    { x: cx, y: 210 },
      http2: { x: cx, y: 350 },
      map1:  { x: cx, y: 140 },
      map2:  { x: cx, y: 280 },
      rmap:  { x: cx + 70, y: 210 },
    };
  }
  const y = h * 0.54;
  return {
    http1: { x: w * 0.10,  y },
    map1:  { x: w * 0.295, y },
    db:    { x: w * 0.50,  y },
    map2:  { x: w * 0.705, y },
    http2: { x: w * 0.90,  y },
    rmap:  { x: w * 0.50,  y: 40 },
  };
};

// ─── Step Pill ───────────────────────────────────────────────────
const Pill = ({ s, on, c }) => (
  <motion.div
    style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '5px 13px', borderRadius: 20,
      background: on ? c.pillActive : 'transparent',
      border: `1px solid ${on ? c.pillAccent : c.pillBorder}`,
      cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
    }}
    animate={{ scale: on ? 1.06 : 1 }}
    transition={{ duration: 0.2 }}
  >
    <span style={{ width: 6, height: 6, borderRadius: '50%',
      background: on ? c.pillAccent : c.pillBorder, flexShrink: 0 }} />
    <span style={{ fontSize: 11.5, fontWeight: on ? 600 : 400,
      color: on ? c.pillAccent : c.pillText,
      fontFamily: 'Montserrat, sans-serif' }}>
      {s.label}
    </span>
  </motion.div>
);

// ─── Mode Toggle ─────────────────────────────────────────────────
const ModeToggle = ({ mode, setMode, c }) => {
  const modes = [
    { key: 'overview', label: 'Overview' },
    { key: 'steps', label: 'Step by Step' },
  ];
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
      <Box sx={{
        display: 'inline-flex', borderRadius: 22, overflow: 'hidden',
        border: `1px solid ${c.pillBorder}`,
        background: c.canvasBg,
        backdropFilter: 'blur(10px)',
      }}>
        {modes.map(m => (
          <Box key={m.key} onClick={() => setMode(m.key)}
            sx={{
              px: 2.5, py: 0.8, cursor: 'pointer', userSelect: 'none',
              fontSize: 12, fontWeight: mode === m.key ? 700 : 400,
              fontFamily: 'Montserrat, sans-serif',
              color: mode === m.key ? c.pillAccent : c.pillText,
              background: mode === m.key ? c.pillActive : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': { background: c.pillActive },
            }}>
            {m.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════
// Main
// ═════════════════════════════════════════════════════════════════
function WorkflowAnimation() {
  const { isLight } = useThemeMode();
  const { ref: wfHeaderRef, revealStyle: wfRevealStyle } = useScrollReveal();
  const theme = useTheme();
  const mob = useMediaQuery(theme.breakpoints.down('md'));
  const c = isLight ? P.light : P.dark;

  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const [mode, setMode] = useState('overview'); // 'steps' | 'overview'
  const [showCanvas, setShowCanvas] = useState(false); // mobile canvas toggle
  const timer = useRef(null);

  const W = 840;
  const H = 310;
  const p = layout(W, H, false);

  const advance = useCallback(() => setStep(s => (s + 1) % STEPS.length), []);

  useEffect(() => {
    if (mode === 'overview' || paused) return;
    const ms = [1600, 1600, 2000, 1600, 2000, 3000];
    timer.current = setTimeout(advance, ms[step]);
    return () => clearTimeout(timer.current);
  }, [step, paused, advance, mode]);

  // Reset to last step when switching to overview
  useEffect(() => {
    if (mode === 'overview') setStep(STEPS.length - 1);
    if (mode === 'steps') { setStep(0); setPaused(false); }
  }, [mode]);

  const isOverview = mode === 'overview';

  // What's visible at each step (overview shows all)
  const v = {
    http1:  isOverview || step >= 0,
    db:     isOverview || step >= 1,
    line1:  isOverview || step >= 2,
    map1:   isOverview || step >= 2,
    http2:  isOverview || step >= 3,
    line2:  isOverview || step >= 4,
    map2:   isOverview || step >= 4,
    ret:    isOverview || step >= 5,
    rmap:   isOverview || step >= 5,
  };

  // Node half-widths for connector positions
  const NHW = 38; // half of 76px node
  const MHW = 20; // half of 40px mapping node
  const MHWS = 16; // half of 32px small mapping

  // Desktop horizontal line segments
  // Forward leg 1: HTTP1.right → Map1.left → Map1.right → DB.left
  // Forward leg 2: DB.right → Map2.left → Map2.right → HTTP2.left
  // Each segment is a FlowLine + Pulse

  return (
    <Box
      component="section"
      id="how-it-works"
      sx={{
        py: { xs: 5, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        background: isLight
          ? 'transparent'
          : 'linear-gradient(35deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      <Container ref={wfHeaderRef} maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 }, ...wfRevealStyle(0) }}>
          <Chip
            icon={<AutoAwesome sx={{ fontSize: '1rem' }} />}
            label="WORKFLOW ORCHESTRATION"
            sx={{
              mb: 2,
              px: 2,
              background: isLight
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(16, 185, 129, 0.15)',
              border: isLight
                ? '1px solid rgba(255, 255, 255, 0.8)'
                : '1px solid rgba(16, 185, 129, 0.4)',
              borderRadius: '50px',
              color: isLight ? '#1e293b' : '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1px',
              '& .MuiChip-icon': {
                color: isLight ? '#6366f1' : '#10b981',
              },
            }}
          />

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.2rem' },
              fontWeight: 900,
              lineHeight: 1.1,
              mb: 1.5,
              color: isLight ? '#0f172a' : '#ffffff',
            }}
          >
            Connect Everything.{' '}
            <Box
              component="span"
              sx={{
                background: isLight
                  ? 'linear-gradient(135deg, #4f46e5, #7c3aed, #06b6d4)'
                  : 'linear-gradient(135deg, #38bdf8, #8b5cf6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Code Nothing.
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              color: isLight
                ? 'rgba(15, 23, 42, 0.65)'
                : 'rgba(248, 250, 252, 0.65)',
              maxWidth: 640,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Link any datasource in seconds — SQL, NoSQL, REST, GraphQL, or custom APIs.
            Alphanetix handles connection pooling, authentication, and automatic fallbacks while you
            build transformation logic visually in the{' '}
            <Box component="span" sx={{ fontWeight: 700, color: isLight ? '#6366f1' : '#a5b4fc' }}>
              Logic Builder
            </Box>.
          </Typography>
        </Box>

        {/* ── 3-Column Layout: Feature Cards | Canvas | Feature Cards ── */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'stretch' },
          gap: { xs: 3, md: 2.5 },
        }}>
          {/* ── Left Feature Cards ── */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            gap: 2,
            flex: '0 0 200px',
            justifyContent: 'center',
          }}>
            {FEATURES.slice(0, 2).map((f, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  p: 2,
                  background: isLight ? 'rgba(255, 255, 255, 0.4)' : 'rgba(99, 102, 241, 0.08)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: isLight
                    ? '1px solid rgba(255, 255, 255, 0.5)'
                    : '1px solid rgba(99, 102, 241, 0.15)',
                  borderRadius: 3,
                  boxShadow: isLight ? '0 8px 32px rgba(99, 102, 241, 0.05)' : 'none',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: isLight ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.35)',
                    boxShadow: isLight ? '0 16px 40px rgba(99, 102, 241, 0.08)' : '0 12px 32px rgba(99, 102, 241, 0.10)',
                  },
                }}
              >
                <Box sx={{
                  mx: 'auto', mb: 1,
                  width: 40, height: 40, borderRadius: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.15)',
                }}>
                  <f.Icon sx={{ fontSize: '1.3rem', color: isLight ? '#6366f1' : '#a5b4fc' }} />
                </Box>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 700, color: isLight ? '#0f172a' : '#ffffff',
                  mb: 0.3, fontSize: '0.8rem', lineHeight: 1.3,
                }}>{f.title}</Typography>
                <Typography variant="body2" sx={{
                  color: isLight ? 'rgba(15, 23, 42, 0.65)' : 'rgba(248, 250, 252, 0.65)',
                  lineHeight: 1.45, fontSize: '0.73rem',
                }}>{f.desc}</Typography>
              </Paper>
            ))}
          </Box>

          {/* ── Center: Toggle + Canvas + Step Info ── */}
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
            <ModeToggle mode={mode} setMode={setMode} c={c} />

            {/* Mobile: "View Workflow" button when canvas is hidden */}
            {mob && !showCanvas && (
              <Box sx={{ textAlign: 'center', my: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<PlayCircleOutline />}
                  onClick={() => setShowCanvas(true)}
                  sx={{
                    borderRadius: 50,
                    px: 3, py: 1.2,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    fontFamily: 'Montserrat, sans-serif',
                    color: isLight ? '#6366f1' : '#a5b4fc',
                    borderColor: isLight ? 'rgba(99, 102, 241,0.3)' : 'rgba(165,180,252,0.3)',
                    background: isLight ? 'rgba(99, 102, 241,0.04)' : 'rgba(99,102,241,0.08)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: isLight ? 'rgba(99, 102, 241,0.5)' : 'rgba(165,180,252,0.5)',
                      background: isLight ? 'rgba(99, 102, 241,0.08)' : 'rgba(99,102,241,0.15)',
                    },
                  }}
                >
                  View Live Workflow
                </Button>
                <Typography sx={{
                  mt: 1, fontSize: '0.72rem',
                  color: isLight ? 'rgba(15,23,42,0.5)' : 'rgba(248,250,252,0.45)',
                }}>
                  Scroll horizontally to explore the full flow
                </Typography>
              </Box>
            )}

            {/* Canvas — always visible on desktop, toggle on mobile */}
            <Collapse in={!mob || showCanvas} timeout={400}>
            <Box sx={{ position: 'relative' }}>
              {/* Mobile close button */}
              {mob && showCanvas && (
                <Box sx={{
                  display: 'flex', justifyContent: 'flex-end',
                  mb: 0.5,
                }}>
                  <Button
                    size="small"
                    onClick={() => setShowCanvas(false)}
                    startIcon={<Close sx={{ fontSize: '0.9rem' }} />}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontFamily: 'Montserrat, sans-serif',
                      color: isLight ? '#64748b' : '#94a3b8',
                      minWidth: 'auto',
                    }}
                  >
                    Close
                  </Button>
                </Box>
              )}
            <Box
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              sx={{
                width: '100%', maxWidth: W, mx: 'auto',
                borderRadius: 4,
                background: c.canvasBg,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${c.canvasBorder}`,
                boxShadow: c.canvasShadow,
                p: { xs: 1, md: 2 },
                overflowX: { xs: 'auto', md: 'visible' },
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': {
                  background: isLight ? 'rgba(99, 102, 241,0.2)' : 'rgba(165,180,252,0.2)',
                  borderRadius: 2,
                },
              }}
            >
              <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', minWidth: mob ? W : undefined }}>
                <Defs c={c} isLight={isLight} />

                {/* ── Forward Leg 1: HTTP1 → Map1 → DB ── */}
                <FlowLine x1={p.http1.x+NHW} y1={p.http1.y} x2={p.map1.x-MHW} y2={p.map1.y} c={c} delay={0.1} vis={v.line1} />
                <FlowLine x1={p.map1.x+MHW} y1={p.map1.y} x2={p.db.x-NHW} y2={p.db.y} c={c} delay={0.3} vis={v.line1} />
                <FlowArrow x={(p.map1.x+MHW + p.db.x-NHW)/2} y={p.map1.y} c={c} delay={0.45} vis={v.line1} />
                <Pulse x1={p.http1.x+NHW} y1={p.http1.y} x2={p.db.x-NHW} y2={p.db.y} c={c} delay={0.6} vis={v.line1} />

                {/* ── Forward Leg 2: DB → Map2 → HTTP2 ── */}
                <FlowLine x1={p.db.x+NHW} y1={p.db.y} x2={p.map2.x-MHW} y2={p.map2.y} c={c} delay={0.1} vis={v.line2} />
                <FlowLine x1={p.map2.x+MHW} y1={p.map2.y} x2={p.http2.x-NHW} y2={p.http2.y} c={c} delay={0.3} vis={v.line2} />
                <FlowArrow x={(p.map2.x+MHW + p.http2.x-NHW)/2} y={p.map2.y} c={c} delay={0.45} vis={v.line2} />
                <Pulse x1={p.db.x+NHW} y1={p.db.y} x2={p.http2.x-NHW} y2={p.http2.y} c={c} delay={0.6} vis={v.line2} />

                {/* ── Return: HTTP2 → rmap → HTTP1 ── */}
                <ReturnArc from={{ x: p.http2.x, y: p.http2.y-NHW }}
                           to={{ x: p.rmap.x+MHWS, y: p.rmap.y }}
                           peakY={p.rmap.y-6} c={c} delay={0.15} vis={v.ret} />
                <ReturnArc from={{ x: p.rmap.x-MHWS, y: p.rmap.y }}
                           to={{ x: p.http1.x, y: p.http1.y-NHW }}
                           peakY={p.rmap.y-6} c={c} delay={0.55} vis={v.ret} />
                {v.ret && (
                  <path id="returnFullPath" fill="none" stroke="none"
                    d={`M${p.http2.x},${p.http2.y-NHW} C${p.http2.x},${p.rmap.y-6} ${p.rmap.x+MHWS},${p.rmap.y-6} ${p.rmap.x+MHWS},${p.rmap.y} L${p.rmap.x-MHWS},${p.rmap.y} C${p.rmap.x-MHWS},${p.rmap.y-6} ${p.http1.x},${p.rmap.y-6} ${p.http1.x},${p.http1.y-NHW}`} />
                )}
                <ReturnPulse c={c} vis={v.ret} />

                {/* ── Nodes ── */}
                <Node x={p.http1.x} y={p.http1.y} type="http" label="Your API"
                  c={c} isLight={isLight} delay={0} vis={v.http1}
                  portR={v.line1} portT={v.ret} />
                <Node x={p.db.x} y={p.db.y} type="database" label="Datasource"
                  c={c} isLight={isLight} delay={0.25} vis={v.db}
                  portL={v.line1} portR={v.line2} />
                <Node x={p.http2.x} y={p.http2.y} type="http" label="Service N"
                  c={c} isLight={isLight} delay={0.25} vis={v.http2}
                  portL={v.line2} portT={v.ret} />

                {/* ── Mapping Nodes (Logic Builder) ── */}
                <MapNode x={p.map1.x} y={p.map1.y} c={c} isLight={isLight} delay={0.4} vis={v.map1} label="Logic Builder" />
                <MapNode x={p.map2.x} y={p.map2.y} c={c} isLight={isLight} delay={0.4} vis={v.map2} label="Logic Builder" />
                <MapNode x={p.rmap.x} y={p.rmap.y} c={c} isLight={isLight} delay={0.4} vis={v.rmap} small label="Response" />
              </svg>
            </Box>
            </Box>
            </Collapse>

            {/* Active step description */}
            {STEPS[step]?.desc && (
              <motion.div key={step}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}>
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: { xs: 'calc(100vw - 48px)', sm: 420, md: 520 },
                    width: '100%',
                    boxSizing: 'border-box',
                    mx: 'auto', mt: { xs: 1.5, md: 2 },
                    p: { xs: 1.5, md: 2 },
                    borderRadius: { xs: 2, md: 3 },
                    background: isLight
                      ? 'rgba(255, 255, 255, 0.4)'
                      : 'rgba(16, 185, 129, 0.08)',
                    backdropFilter: isLight ? 'blur(24px)' : undefined,
                    WebkitBackdropFilter: isLight ? 'blur(24px)' : undefined,
                    border: isLight
                      ? '1px solid rgba(255, 255, 255, 0.5)'
                      : '1px solid rgba(16, 185, 129, 0.25)',
                    boxShadow: isLight
                      ? '0 8px 32px rgba(99, 102, 241, 0.06)'
                      : 'none',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{
                    fontSize: { xs: '0.75rem', md: '0.82rem' }, fontWeight: 700,
                    color: isLight ? '#6366f1' : '#a5b4fc', mb: 0.3,
                  }}>{STEPS[step].label}</Typography>
                  <Typography variant="body2" sx={{
                    color: isLight ? 'rgba(30, 41, 59, 0.75)' : 'rgba(248, 250, 252, 0.75)',
                    fontSize: { xs: '0.73rem', md: '0.82rem' }, lineHeight: 1.45,
                  }}>{STEPS[step].desc}</Typography>
                </Paper>
              </motion.div>
            )}

            {/* Step pills — only in step-by-step mode */}
            {mode === 'steps' && (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 2 }}>
                  {STEPS.map((s, i) => (
                    <Box key={s.id} onClick={() => { setStep(i); setPaused(true); }}>
                      <Pill s={s} on={step === i} c={c} />
                    </Box>
                  ))}
                </Box>
                <Typography align="center" sx={{ mt: 1, fontSize: 11, color: c.sub, opacity: 0.55 }}>
                  {paused ? 'Click a step or move away to resume' : 'Hover to pause · Click step to jump'}
                </Typography>
              </>
            )}
          </Box>

          {/* ── Right Feature Cards (desktop only) ── */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            gap: 2,
            flex: '0 0 200px',
            justifyContent: 'center',
          }}>
            {FEATURES.slice(2, 4).map((f, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  p: 2,
                  background: isLight ? 'rgba(255, 255, 255, 0.4)' : 'rgba(99, 102, 241, 0.08)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: isLight
                    ? '1px solid rgba(255, 255, 255, 0.5)'
                    : '1px solid rgba(99, 102, 241, 0.15)',
                  borderRadius: 3,
                  boxShadow: isLight ? '0 8px 32px rgba(99, 102, 241, 0.05)' : 'none',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: isLight ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.35)',
                    boxShadow: isLight ? '0 16px 40px rgba(99, 102, 241, 0.08)' : '0 12px 32px rgba(99, 102, 241, 0.10)',
                  },
                }}
              >
                <Box sx={{
                  mx: 'auto', mb: 1,
                  width: 40, height: 40, borderRadius: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.15)',
                }}>
                  <f.Icon sx={{ fontSize: '1.3rem', color: isLight ? '#6366f1' : '#a5b4fc' }} />
                </Box>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 700, color: isLight ? '#0f172a' : '#ffffff',
                  mb: 0.3, fontSize: '0.8rem', lineHeight: 1.3,
                }}>{f.title}</Typography>
                <Typography variant="body2" sx={{
                  color: isLight ? 'rgba(15, 23, 42, 0.65)' : 'rgba(248, 250, 252, 0.65)',
                  lineHeight: 1.45, fontSize: '0.73rem',
                }}>{f.desc}</Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* ── Mobile Feature Cards (below canvas) ── */}
        <Box sx={{
          display: { xs: 'grid', md: 'none' },
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
          mt: 3,
        }}>
          {FEATURES.map((f, i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 2,
                background: isLight ? 'rgba(255, 255, 255, 0.4)' : 'rgba(99, 102, 241, 0.08)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: isLight
                  ? '1px solid rgba(255, 255, 255, 0.5)'
                  : '1px solid rgba(99, 102, 241, 0.15)',
                borderRadius: 3,
                boxShadow: isLight ? '0 8px 32px rgba(99, 102, 241, 0.05)' : 'none',
                textAlign: 'center',
              }}
            >
              <Box sx={{
                mx: 'auto', mb: 1,
                width: 36, height: 36, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.15)',
              }}>
                <f.Icon sx={{ fontSize: '1.2rem', color: isLight ? '#6366f1' : '#a5b4fc' }} />
              </Box>
              <Typography variant="subtitle2" sx={{
                fontWeight: 700, color: isLight ? '#0f172a' : '#ffffff',
                mb: 0.3, fontSize: '0.78rem', lineHeight: 1.3,
              }}>{f.title}</Typography>
              <Typography variant="body2" sx={{
                color: isLight ? 'rgba(15, 23, 42, 0.65)' : 'rgba(248, 250, 252, 0.65)',
                lineHeight: 1.4, fontSize: '0.7rem',
              }}>{f.desc}</Typography>
            </Paper>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default WorkflowAnimation;
